// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import ClsodureRequestService from "../../closure-request.service";
import ClosureRequest from "../../../../models/closure-request.model";
import clsx from "clsx";
import { Status } from "../../../../models/enum";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import {
  ClosureDetailsModal,
  useClosureDetails,
} from "../closure-details-modal/closure-details-modal";

const Completed: React.FC = (): ReactElement => {
  const closureRequestService = new ClsodureRequestService();
  const loadingState = useSelector((state: any) => state?.loading);
  const [closureRequestList, setClosureRequestList] = useState<
    ClosureRequest[]
  >([]);

  const {
    closureDetails,
    viewDetailsModal,
    fetchClosureDetails,
    closeDetailsModal,
  } = useClosureDetails();

  useEffect(() => {
    fetchClosureRequestListData();
  }, []);

  const fetchClosureRequestListData = async (): Promise<void> => {
    const data = await closureRequestService.getClosureRequestListData([
      "Approved",
    ]);
    if (data !== undefined) {
      setClosureRequestList(data);
    }
  };

  const columns: ColumnsType<ClosureRequest> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      sorter: (a: ClosureRequest, b: ClosureRequest): number => a.id - b.id,
      fixed: "left",
    },
    {
      title: "Client name",
      dataIndex: "clientName",
      key: "clientName",
      sorter: (a: ClosureRequest, b: ClosureRequest): number =>
        a.user?.firstName?.length - b.user?.firstName?.length,
      fixed: "left",
      render: (_, record: ClosureRequest): ReactElement => (
        <Link to={`/home/customer-profile/${record.user?.id}`}>
          {record.user?.firstName + " " + record?.user?.lastName}
        </Link>
      ),
    },
    {
      title: "Account number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      sorter: (a: ClosureRequest, b: ClosureRequest): number =>
        a.account.accountCode - b.account.accountCode,
      render: (_, record: ClosureRequest): ReactElement => (
        <span>{record.account.accountCode}</span>
      ),
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Feedback",
      dataIndex: "feedback",
      key: "feedback",
      sorter: (a: ClosureRequest, b: ClosureRequest): number =>
        a.feedback?.length -
        b.feedback?.length,
      render: (_, record: ClosureRequest): ReactElement => (
        <span>{record.feedback}</span>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "souce",
      render: (text) =>
        text === "Portal" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: ClosureRequest, b: ClosureRequest): number =>
        a.createdAt.length - b.createdAt.length,
      render: (text: string): ReactElement => (
        <span className=" text-gray-500">
          {moment(text).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Request status",
      dataIndex: "status",
      key: "status",
      width: 180,
      sorter: (a: ClosureRequest, b: ClosureRequest) =>
        a.status.length - b.status.length,
      filters: [
        {
          text: "Approved",
          value: "Approved",
        },
        {
          text: "Rejected",
          value: "Rejected",
        },
      ],
      render: (text: Status) => (
        <span
          className={clsx(
            "rounded bg-gray-600 text-xs font-medium w-fit py-2 px-3",
            text === Status.Approved
              ? "bg-gray-600 text-green-500"
              : "bg-pink-200 text-pink-300"
          )}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Status notes",
      dataIndex: "statusNotes",
      key: "statusNotes",
      sorter: (a: ClosureRequest, b: ClosureRequest) =>
        a.statusNotes.length - b.statusNotes.length,
    },
    {
      title: "Closure forms",
      dataIndex: "closureForms",
      width: 100,
      key: "closureForms",
      render: (_, record: ClosureRequest): ReactElement => (
        <div className="flex items-center gap-3">
          <ReactSVG
            src={"/images/view.svg"}
            className="cursor-pointer"
            onClick={() => fetchClosureDetails(record.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-t-lg rounded-e-lg bg-white py-3 px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? closureRequestList?.length : ""} completed requests
        </p>
        <div className="flex items-center">
          {/* <CustomSearch />
          <Tooltip placement="topLeft" title="Columns settings">
            <img src={"/images/grid.svg"} alt="grid" />
          </Tooltip> */}
        </div>
      </div>
      {closureRequestList?.length ? (
        <CustomTable
          columns={columns}
          data={closureRequestList}
          showPagination={false}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}

      {/* View Closure Details Modal */}
      <ClosureDetailsModal
        isVisible={viewDetailsModal}
        closureDetails={closureDetails}
        onClose={closeDetailsModal}
      />
    </div>
  );
};

export default Completed;
